import React, {Component, ReactNode} from 'react'
import {Nullable} from '../../app/TypeHelpers'
import {VOTE_STATUS, VOTING_PREFERENCE_RESPONSE} from '../../CodeMaster'
import ResidentGeneralMeetingRepo, {ResidentProposalResponse} from './ResidentGeneralMeetingRepo'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {HideInPC, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import PPStepper from '../shared/PPStepper'
import {Box} from '@mui/material'
import {votingPreferenceStepsForPc, votingPreferenceStepsForSp} from './ResidentVotingPreferenceMethodSelectionPage'
import {PlainStyledButton, PrimaryStyledButton, StyledSecondaryButton} from '../../styledComponents/Button'
import {PlainBackLink} from '../../styledComponents/Links'
import {DisplayItemDividerForPageTop, DisplayItemTitle} from '../../styledComponents/DisplayItem'
import styled from 'styled-components'
import ResidentSectionComponent from '../shared/ResidentSectionComponent'
import {GeneralMeetingProposal} from './model/GeneralMeetingProposal'
import {WideMainContainer} from '../../styledComponents/ContentsArea'
import {FullWidthColumnFlexContainer, LeftRowFlexContainer} from '../../styledComponents/FlexComponents'
import {SecondaryTag} from '../../styledComponents/Tag'
import {Colors, FontWeights} from '../../styledComponents/Constants'
import {
    ResidentVotingPreferenceProposalResponseConfirmationLocationState
} from './ResidentVotingPreferenceProposalResponseConfirmationPage'
import {Location} from 'react-router-dom'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../shared/hoc/createResidentWithErrorHandlingPage'
import {HttpError} from '../../network/errors/HttpError'

export interface ResidentVotingPreferenceProposalResponseInputLocationState {
    votingPreferenceId: Nullable<number>
    assocId: number
    assocName: string
    votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE
    residentProposalResponseList: Nullable<ResidentProposalResponse[]>
    votingPreferenceVersion: Nullable<number>
    generalMeetingProposalList: GeneralMeetingProposal[]
    beforeNetRespondedAssocIdList: number[]
}

interface Props {
    location: Location<ResidentVotingPreferenceProposalResponseInputLocationState>
    condominiumCd: string
    generalMeetingId: number
    assocId: number
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    residentProposalResponseList: ResidentProposalResponse[]
}

class ResidentVotingPreferenceProposalResponseInputPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        if (!this.props.location.state) {
            this.props.navigate('/resident/error')
        }

        const {
            residentProposalResponseList: initialResidentProposalResponseList,
            generalMeetingProposalList,
        } = this.props.location.state

        const residentProposalResponseList: ResidentProposalResponse[] = generalMeetingProposalList
            .map(generalMeetingProposal => {
                    if (!generalMeetingProposal.proposalId) {
                        throw Error('議案IDが不正')
                    }

                    return {
                        proposalId: generalMeetingProposal.proposalId,
                        proposalResponse:
                            initialResidentProposalResponseList?.find(
                                (residentProposalResponse: ResidentProposalResponse) =>
                                    residentProposalResponse.proposalId === generalMeetingProposal.proposalId
                            )?.proposalResponse || VOTE_STATUS.ABSTAIN,
                    }
                }
            )

        this.state = {
            residentProposalResponseList: residentProposalResponseList
        }

        this.handleBackButtonClick = this.handleBackButtonClick.bind(this)
        this.handleProposalVoteButtonClick = this.handleProposalVoteButtonClick.bind(this)
        this.handleVoteButtonClick = this.handleVoteButtonClick.bind(this)
        this.handleAllForButtonClick = this.handleAllForButtonClick.bind(this)
        this.renderProposalVotingArea = this.renderProposalVotingArea.bind(this)
    }

    handleBackButtonClick(): void {
        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            navigate
        } = this.props

        navigate(
            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/method-selection`,
        )
    }

    handleProposalVoteButtonClick(proposalId: number, proposalResponse: VOTE_STATUS): void {
        this.setState((prevState) => ({
            residentProposalResponseList: prevState.residentProposalResponseList
                .map(response => {
                    if (response.proposalId !== proposalId) {
                        return response
                    }
                    // 既に選択済みの同じボタンを再押下した場合は棄権に戻す
                    const nextResponse = response.proposalResponse.value !== proposalResponse.value
                        ? proposalResponse
                        : VOTE_STATUS.ABSTAIN
                    return {
                        ...response,
                        proposalResponse: nextResponse,
                    }
                }),
        }))
    }

    async handleVoteButtonClick(): Promise<void> {
        if (!this.props.location.state) {
            return
        }

        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            residentGeneralMeetingRepo,
            navigate,
            eventEmitter,
        } = this.props

        const {
            votingPreferenceId,
            assocName,
            votingPreferenceResponse,
            votingPreferenceVersion,
            generalMeetingProposalList,
            beforeNetRespondedAssocIdList,
        } = this.props.location.state

        const {
            residentProposalResponseList,
        } = this.state

        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                // @ts-ignore
                residentGeneralMeetingRepo.saveGeneralMeetingVotingPreference.bind(
                    residentGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId,
                    assocId,
                    votingPreferenceId,
                    votingPreferenceResponse,
                    null,
                    residentProposalResponseList,
                    votingPreferenceVersion,
                    false,
                ),
                async (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError instanceof HttpError) {
                        navigate(
                            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
                            {
                                state: {
                                    votingPreferenceHttpError: {
                                        status: httpErrorOrNetworkError.status,
                                        responseBody: httpErrorOrNetworkError.responseBody,
                                    },
                                },
                            },
                        )
                    }
                },
            )

            if (!response || response.requestError !== null) {
                return
            }

            const locationState: ResidentVotingPreferenceProposalResponseConfirmationLocationState = {
                votingPreferenceId,
                assocId,
                assocName,
                votingPreferenceResponse,
                votingPreferenceVersion,
                residentProposalResponseList,
                generalMeetingProposalList,
                beforeNetRespondedAssocIdList,
            }

            navigate(
                `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/confirmation/proposal-response`,
                {
                    state: locationState,
                },
            )
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleAllForButtonClick(): void {
        this.setState((prevState) => ({
            residentProposalResponseList: prevState.residentProposalResponseList
                .map(response => ({
                    ...response,
                    proposalResponse: VOTE_STATUS.FOR,
                }))
        }))
    }

    renderProposalVotingArea(generalMeetingProposal: GeneralMeetingProposal): ReactNode {
        const {
            proposalId,
            proposalNumber,
            resolutionType,
            proposalName
        } = generalMeetingProposal

        if (proposalId === null || !resolutionType || !proposalName) {
            return
        }

        const {
            residentProposalResponseList,
        } = this.state

        const residentProposalResponse = residentProposalResponseList
            .find((response) => (
                response.proposalId === proposalId
            ))

        if (!residentProposalResponse) {
            throw Error('議案への回答がない')
        }

        return <ProposalVotingArea key={proposalId}>
            <LeftRowFlexContainer>
                <div>第{proposalNumber}号議案</div>
                <Box width={'10px'}/>
                <SecondaryTag>{resolutionType.label}</SecondaryTag>
            </LeftRowFlexContainer>
            <ProposalName>{proposalName}</ProposalName>
            <ProposalVotingActionArea>
                <ForVoteButton
                    status={residentProposalResponse.proposalResponse}
                    onClick={() => this.handleProposalVoteButtonClick(proposalId, VOTE_STATUS.FOR)}
                >
                    賛成
                </ForVoteButton>
                <AgainstVoteButton
                    status={residentProposalResponse.proposalResponse}
                    onClick={() => this.handleProposalVoteButtonClick(proposalId, VOTE_STATUS.AGAINST)}
                >
                    反対
                </AgainstVoteButton>
            </ProposalVotingActionArea>
        </ProposalVotingArea>
    }

    render(): ReactNode {
        if (!this.props.location.state) {
            return null
        }

        const {
            condominiumCd,
            generalMeetingId,
            assocId,
        } = this.props

        const {
            assocName,
            generalMeetingProposalList,
        } = this.props.location.state

        return <>
            <PlainBackLink
                to={`/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/method-selection`}
            >
                戻る
            </PlainBackLink>
            <StyledDisplayItemDividerForPageTop/>
            <HideInSP>
                <PPStepper steps={votingPreferenceStepsForPc} activeStep={1}/>
            </HideInSP>
            <HideInPC>
                <PPStepper steps={votingPreferenceStepsForSp} activeStep={1} alternativeLabel/>
            </HideInPC>
            <Box height={'30px'}/>
            <StyledPageTitle>議決権の行使</StyledPageTitle>
            <Box height={'20px'}/>
            <div>
                管理組合の総会開催要項をご確認のうえ、議案に対して賛成・反対を選択し、議決権を行使してください。
            </div>
            <Box height={'20px'}/>
            <ResidentSectionComponent
                title={'対象管理組合'}
                content={<AssocName>{assocName}</AssocName>}
            />
            <Box height={'20px'}/>
            <DisplayItemTitle>議案</DisplayItemTitle>
            <Box height={'17px'}/>
            <AllForButtonArea>
                <StyledSecondaryButton onClick={this.handleAllForButtonClick}>
                    すべての議案に賛成する
                </StyledSecondaryButton>
            </AllForButtonArea>
            <Box height={'5px'}/>
            <ProposalVotingContainer>
                {generalMeetingProposalList.map(generalMeetingProposal => (
                    this.renderProposalVotingArea(generalMeetingProposal)
                ))}
            </ProposalVotingContainer>
            <Box height={'20px'}/>
            <PrimaryStyledButton onClick={this.handleVoteButtonClick}>
                議決権を行使する
            </PrimaryStyledButton>
            <Box height={'30px'}/>
            <StyledSecondaryButton onClick={this.handleBackButtonClick}>
                戻る
            </StyledSecondaryButton>
            <Box height={'15px'}/>
        </>
    }
}

export default createResidentWithErrorHandlingPage(ResidentVotingPreferenceProposalResponseInputPage)

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const AssocName = styled.div`
  font-weight: ${FontWeights.Normal};
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const AllForButtonArea = styled(WideMainContainer)`
  padding: 10px 15px;
`

const ProposalVotingContainer = styled(FullWidthColumnFlexContainer)`
  align-items: stretch;
  gap: 5px;
`

const ProposalVotingArea = styled(WideMainContainer)`
  padding: 20px 15px;
`

const ProposalName = styled.div`
  font-weight: ${FontWeights.Bold};
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const ProposalVotingActionArea = styled(LeftRowFlexContainer)`
  margin-top: 20px;
  gap: 25px;
`

const VoteButton = styled(PlainStyledButton)`
  max-width: 160px;
  width: 100%;
  height: 50px;
  border-radius: 8px;
  color: ${Colors.Body};
  background-color: ${Colors.Background};
  border: 1px solid ${Colors.Background};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2),
  0 2px 1px rgba(0, 0, 0, 0.12),
  0 1px 1px rgba(0, 0, 0, 0.14);
  box-sizing: border-box;
`

const ForVoteButton = styled(VoteButton)<{ status: VOTE_STATUS }>`
  ${props => props.status.value === VOTE_STATUS.FOR.value && `
    border: 5px solid #00838A;
    color: #00838A;
    font-weight: bold;
  `}
`

const AgainstVoteButton = styled(VoteButton)<{ status: VOTE_STATUS }>`
  ${props => props.status.value === VOTE_STATUS.AGAINST.value && `
    border: 5px solid #D7374A;
    color: #D7374A;
    font-weight: bold;
  `}
`
